<template>
    <div
      class="map-container"
      style="height: 500px; width: 100%; margin-top: 24px"
    >
      <p class="map-view-notice">This map is best viewed on desktop</p>
      <l-map
        :center="[37.8, -96]"
        :zoom="4"
        style="height: 500px; z-index: 0"
        :options="mapOptions"
        :key="mapId"
      >
        <l-geo-json
          :geojson="geojson"
          :options="geojsonOptions"
          ref="geolayer"
        ></l-geo-json>
  
        <l-control position="bottomleft" v-if="!zeroBills">
          <div class="info legend">
            <div v-for="(color, index) in mapData.colors" :key="color">
              <i :style="`background: ${color}`" />{{ mapData.labels[index] }}
            </div>
          </div>
        </l-control>
  
        <l-control position="topleft" v-if="zeroBills">
          <div class="info">
            No states contain bills which meet this criteria.
          </div>
        </l-control>
        <l-control position="topleft" v-else>
          <div v-if="highlighted" class="info">
            {{ highlighted.name }} has {{ highlighted.bills }}
            {{ highlighted.bills !== 1 ? "bills" : "bill" }}.
          </div>
          <div v-else class="info">
            Hover over a state to see its bill count for this search criteria.
          </div>
        </l-control>
  
        <l-control position="bottomright">
          <img src="../../src/assets/images/bp-logo.svg" with="225" height="38"
        /></l-control>
  
        <l-control position="topright">
          <slot></slot>
        </l-control>
      </l-map>
      <!-- <pre style="white-space: pre-wrap">{{ mapDataStr }}</pre> -->
    </div>
  </template>
  
  <script>
  import { L, LMap, LGeoJson } from "vue2-leaflet";
  import geojson from "../assets/data/us-states.json";
  
  const DEFAULT_GRADE_COUNT = 4;
  
  export default {
    props: ["states"],
    watch: {
      states() {
        this.mapId++;
      },
    },
    mounted() {
        console.log("FiftyStates mounted")
    },
    data() {
      return {
        mapId: 1,
        highlighted: null,
        geojson: geojson,
        geojsonOptions: {
          style: (feature) => {
            return {
              fillColor: this.getColor(feature.properties.abbr),
              weight: 2,
              opacity: 1,
              color: "#c5c9ca",
              dashArray: "3",
              fillOpacity: 0.7,
            };
          },
          onEachFeature: (feature, layer) => {
            layer.on({
              mouseover: this.highlightFeature.bind(this),
              mouseout: this.resetHighlight.bind(this),
            });
  
            layer.addEventListener("click", () => {
              layer.bindPopup(
                `<div class="popup" onclick="goToState('${feature.properties.abbr}')">Click here to see these bills in ${feature.properties.name}</div>`
              );
            });
          },
        },
        mapOptions: {
          attributionControl: false,
          zoomControl: false,
          dragging: false,
          doubleClickZoom: false,
          boxZoom: false,
          scrollWheelZoom: false,
        },
      };
    },
    methods: {
      getColor(abbr) {
        const state = this.states.find((state) => state.abbr === abbr);
        const bills = Number(state?.bills ?? 0);
        if (bills === 0) return "#ffffff";
        for (
          let gradeIndex = this.mapData.grades.length - 1;
          gradeIndex > -1;
          gradeIndex--
        ) {
          const grade = this.mapData.grades[gradeIndex];
          if (bills >= grade) {
            return this.mapData.colors[gradeIndex];
          }
        }
        return "#ffffff";
      },
      highlightFeature(e) {
        var feature = e.target;
        feature.setStyle({
          weight: 3,
          color: "#666",
          dashArray: "",
          fillOpacity: 0.7,
        });
  
        const { abbr, name } = e.target.feature.properties;
        const state = this.states.find((state) => state.abbr === abbr);
        const bills = Number(state?.bills ?? 0);
        this.highlighted = { name, bills };
      },
      resetHighlight(e) {
        var layer = e.target;
        layer.setStyle({
          weight: 2,
          opacity: 1,
          color: "#d4dadc",
          dashArray: "3",
          fillOpacity: 0.7,
        });
        this.highlighted = null;
      },
    },
    computed: {
      zeroBills() {
        return this.states.filter((s) => s.bills > 0).length === 0;
      },
      grades() {
        return this.states.map((s) => s.bills);
      },
      mapData() {
        if (this.states.filter((s) => s.bills > 0).length === 0) {
          return {
            grades: null,
            labels: null,
            colors: null,
          };
        }
  
        let sorted = this.states
          .map((s) => s.bills)
          .filter((n) => n > 0)
          .sort(sortNumbers);
  
        if (sorted.length === 0) {
          return {
            grades: [],
            labels: [`0`],
            colors: ["#ffffff"],
          };
        }
  
        let gradeCount = DEFAULT_GRADE_COUNT;
        if (sorted.length < 18) {
          gradeCount--;
        }
        if (sorted.length < 8) {
          gradeCount--;
        }
  
        let width = sorted.length / gradeCount;
  
        let breaks = arrayRange(1, gradeCount - 1, 1).map((n) =>
          Math.round(n * width)
        );
  
        let ranges = [];
  
        if (this.states.length > sorted.length) {
          ranges.push({ type: "zero" });
        }
  
        let addRange = (low, high) => {
          if (low === high) {
            ranges.push({ type: "single", value: low });
          } else {
            ranges.push({ type: "range", range: [low, high] });
          }
        };
  
        addRange(sorted[0], sorted[breaks[0] - 1]);
  
        for (let gradeIndex = 1; gradeIndex <= gradeCount; gradeIndex++) {
          const isLastGrade = gradeIndex + 1 === gradeCount;
          const breakIndex = gradeIndex - 1;
          const currentIndex = breaks[breakIndex];
          const currentValue = sorted[currentIndex];
          const previousValue = sorted[currentIndex - 1];
          const lastIndex = isLastGrade
            ? sorted.length - 1
            : breaks[breakIndex + 1] - 1;
          const lastValue = sorted[lastIndex];
  
          // console.log({
          //   gradeIndex,
          //   isLastGrade,
          //   breakIndex,
          //   currentIndex,
          //   currentValue,
          //   previousValue,
          //   lastIndex,
          //   lastValue,
          // });
          if (currentValue > previousValue) {
            // console.log(
            //   `${currentValue}-${lastValue} because ${currentValue} > ${previousValue}`
            // );
            addRange(currentValue, lastValue);
          } else if (currentValue) {
            const next = sorted.slice(currentIndex, lastIndex).find((n) => {
              const lastRange = ranges[ranges.length - 1];
              if (lastRange.type === "range") {
                return n > ranges[ranges.length - 1]["range"][1];
              } else {
                return n > ranges[ranges.length - 1].value;
              }
            });
            if (next) {
              // console.log({ next, lastValue, currentIndex, lastIndex });
              addRange(next, lastValue);
            }
          }
        }
  
        let grades = ranges.map(({ type, range, value }) => {
          if (type === "zero") return 0;
          if (type === "single") return value;
          return range[0];
        });
  
        let labels = ranges.map(({ type, range, value }) => {
          if (type === "zero") return "0";
          if (type === "single") return `${value}`;
          return `${range[0]}-${range[1]}`;
        });
  
        let colorCount = ranges.reduce(
          (acc, { type }) => acc + (type === "zero" ? 0 : 1),
          0
        );
  
        let colors = [];
        if (ranges[0].type === "zero") {
          colors.push("#ffffff");
        }
        switch (colorCount) {
          case 4: {
            colors.push("#00AAAA", "#007777", "#004444", "#001111");
            break;
          }
          case 3: {
            colors.push("#00AAAA", "#006666", "#001111");
            break;
          }
          case 2: {
            colors.push("#00AAAA", "#001111");
            break;
          }
          case 1: {
            colors.push("#00AAAA");
            break;
          }
        }
  
        return {
          grades,
          labels,
          colors,
        };
      },
      mapDataStr() {
        return JSON.stringify(this.mapData, null, 2);
      },
      statesStr() {
        return JSON.stringify(this.states, null, 2);
      },
    },
  };
  
  function arrayRange(start, stop, step) {
    return Array.from(
      { length: (stop - start) / step + 1 },
      (_, index) => start + index * step
    );
  }
  
  function sortNumbers(a, b) {
    return a - b;
  }
  </script>
  
  <style>
  .info {
    padding: 6px 8px;
    font: Libre Franklin, sans-serif;
    background: white;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    font-size: 14px;
    max-width: 240px;
    max-height: 400px;
    overflow-y: scroll;
  }
  
  .info h4 {
    margin: 0 0 5px;
    color: #777;
  }
  
  .popup {
    padding: 6px 8px;
    font: Libre Franklin, sans-serif;
    font-size: 14px;
    max-width: 240px;
    cursor: pointer;
  }
  
  /* making the legend */
  .legend {
    text-align: left;
    line-height: 24px;
    color: #555;
    font: Libre Franklin, sans-serif;
  }
  
  .legend i {
    width: 24px;
    height: 24px;
    float: left;
    margin-right: 8px;
    opacity: 0.7;
  }
  
  .logo {
    width: 40px;
    height: 40px;
    margin-right: 8px;
  }
  
  .map-container .leaflet-container {
    background: #eeffff;
    outline: 0;
    border: 1px solid black;
  }
  
  .map-view-notice {
    display: none;
  }
  
  @media screen and (max-width: 992px) {
    .map-view-notice {
      display: block;
    }
  }
  </style>
  